import { Controller } from "@hotwired/stimulus"
import serialize from "form-serialize"
import TomSelect from "@lib/custom-tom-select"
import { post } from "@lib/requests"

// Connects to data-controller="new-message"
export default class extends Controller {
  
  static targets = [
    "form", "subject", "content", "recipientsBlock", "clinicBlock", "speciesBlock", "clientBlock", "clientSelect", "template", "saveButton", "sendButton"
  ]
  
  connect() {
    this.setupClientSelect()
    this.updateRecipients()
  }

  setupClientSelect() {
    // Destroy existing Tom Select instance if it exists (e.g., from previous connection)
    if (this.clientSelectTarget.tomselect) {
      this.clientSelectTarget.tomselect.destroy()
    }

    this.select = new TomSelect(this.clientSelectTarget, {
      maxItems: 1,
      load: this.loadClients.bind(this),
      labelField: "name",
      valueField: "id",
      searchField: "name",
      onItemAdd: this.onClientSelect.bind(this),
      shouldLoad: function(query) {
        return true;  // Always trigger the load function (so we can clear the options on blank queries)
      }
    })
  }

  disconnect() {
    if (this.select) {
      this.select.destroy()
    }
  }
  
  async loadClients(query, callback) {
    this.select.clearSelect()
    if (!query.trim()) {
      callback([])
      return
    }
    const queryUrl = `/api/v1/clients?q=${query}`
    const response = await fetch(queryUrl, { headers: { accept: "application/json" } })
    const results = await response.json()
    callback(results || [])
  }
  
  // When a client is selected, we will add a hidden selected_client_ids[] input to the form, then update the recipients
  // This is done as we don't really want to keep the selected options inside the select box (cumbersome)
  onClientSelect(value, item) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = "message[selected_client_ids][]"
    input.value = value
    this.formTarget.appendChild(input)
    this.select.clearSelect()
    this.updateRecipients()
  }
  
  get editorInstance() {
    return this.contentTarget?.editor
  }

  get formData() {
    const data = serialize(this.formTarget, { hash: true })
    const { clinic_ids, selected_client_ids, species_ids } = data.message
    return { clinic_ids, selected_client_ids, species_ids }
  }
  
  insertPlaceholder(event) {
    const placeholder = event.target.dataset.placeholder
    const value = event.target.textContent
    if (!placeholder) return
    const placeholderText = `[[${placeholder}]]`
    this.editorInstance.insertString(placeholderText)
  }

  clearCheckboxes() {
    const checkboxes = this.formTarget.querySelectorAll("input[type=checkbox]")
    checkboxes.forEach(checkbox => checkbox.checked = false)
  }

  clearClientIds() {
    const clientIds = this.formTarget.querySelectorAll("input[name='message[selected_client_ids][]']")
    clientIds.forEach(clientId => clientId.remove())
  }
  
  changeMode(e) {
    if (e.target.value == "client") {
      this.clinicBlockTarget.classList.add("hidden")
      this.speciesBlockTarget.classList.add("hidden")
      this.clientBlockTarget.classList.remove("hidden")
      this.select.focus()
    } else {
      this.clearClientIds()
      this.clinicBlockTarget.classList.remove("hidden")
      this.speciesBlockTarget.classList.remove("hidden")
      this.clientBlockTarget.classList.add("hidden")
    }
    this.clearCheckboxes()
    this.updateRecipients()
  }

  async updateRecipients(e) {
    // First disable the save/send buttons in case it's not desired while we await the response
    this.disableButtons()
    
    await post("/messages/recipients", { body: this.formData, responseKind: "turbo-stream" })
    
    // If the #message-recipients element has a #no-results element it means there are recipients
    setTimeout(() => {
      const noResults = this.element.querySelector("#no-results")
      if (!noResults) this.enableButtons()
    }, 300)
  }

  disableButtons() {
    this.saveButtonTarget.disabled = true
    this.sendButtonTarget.disabled = true
  }

  enableButtons() {
    this.saveButtonTarget.disabled = false
    this.sendButtonTarget.disabled = false
  }

  toggleTemplate(e) {
    if (e.target.checked) {
      const input = this.templateTarget.querySelector("input")
      this.templateTarget.classList.remove("hidden")
      input.value = this.subjectTarget.value
      input.focus()
    } else {
      this.templateTarget.classList.add("hidden")
      this.templateTarget.querySelector("input").value = ""
    }
  }

  loadTemplate(e) {
    const button = e.target
    button.previousElementSibling.showModal()
  }

  async insertTemplate(e) {
    const button = e.currentTarget
    const subject = button.dataset.subject
    const content = button.dataset.content
    this.subjectTarget.value = subject
    this.editorInstance.loadHTML(content)
    button.closest("dialog").close()
  }

}
